<template>

	<div class="flex justify-center items-center relative m-1"
		 :class="( border ) ? 'border border-gray-300' : ''"
		 @mouseover="hoveredKey = index"
		 @mouseout="hoveredKey = null">
		<canvas ref="fabric" class="absolute"></canvas>
		<a href="javascript:void(0);" @click="download" class="button-primary p-4 absolute"
		   :class="hoveredKey !== index ? '!invisible' : ''">
			Download
			<i class="fa fa-fw fa-long-arrow-right"></i>
		</a>
	</div>

</template>

<script>

	import {fabric} from "fabric";
	import FontLoaderClass from "../../classes/FontLoader";
	import ScriptLoaderClass from '../../classes/ScriptLoader';

	// Set default point to center of object
	fabric.Object.prototype.originX = 'center';
	fabric.Object.prototype.originY = 'center';

	// Set default corner style
	fabric.Object.prototype.transparentCorners = false; // corners are all fully visible
	fabric.Object.prototype.cornerStyle = 'circle';
	fabric.Object.prototype.cornerSize = 15;
	fabric.Object.prototype.borderColor = '#4a90e2';
	fabric.Object.prototype.cornerColor = '#4a90e2';
	fabric.Object.prototype.preserveOpacity = true;
	fabric.Object.prototype.crossOrigin = 'anonymous';

	// When fabric objects are cached, overflowing signatures get cut off
	fabric.Object.prototype.objectCaching = false;

	// Disable flipping, set centered scaling
	fabric.Object.prototype.centeredScaling = true; // scale all objects from center position
	fabric.Object.prototype.lockUniScaling = true; // scale all objects proportionally
	fabric.Object.prototype.lockScalingFlip = true; // prevent flipping object when scaling

	// Hide rotating point and scaling points
	fabric.Object.prototype.setControlsVisibility({
		mtr: false,
		mt: false, // Middle Top
		mb: false, // Middle Bottom
		ml: false, // Middle Left
		mr: false, // Middle Right
		tr: false, // Top Right, replaced with custom rotate control
		tl: false, // Top Left, already exists bottom right, same function
		bl: false, // Bottom Left, replaced with custom delete control
	});

	window.FontLoader = new FontLoaderClass;
	window.ScriptLoader = new ScriptLoaderClass;

	export default {

		props: {
			initialTextFonts: Array,
			initialSignatureFonts: Array,
			name: String,
			tagline: String,
			attributes: Object,
			canvasWidth: Number,
			canvasHeight: Number,
			border: Boolean,
			index: String,
			funnel: String,
		},

		watch: {
			name: {
				handler(newVal, oldVal)
				{
					this.updateAll();
				},
			},
			tagline: {
				handler(newVal, oldVal)
				{
					this.updateAll();
				},
			}
		},

		data()
		{
			return {
				fabricCanvas: null,
				count: 2, // signature + tagline
				signatureContext: null, // stored 2d context of signature canvas
				signatureBaseline: 0, // signature baseline y position
				taglineWidth: 0,
				taglineHeight: 0,
				imageDataToScan: [],
				hoveredKey: null
			}
		},

		computed: {

			getObjects()
			{
				return this.fabricCanvas.getObjects();
			}

		},

		methods: {

			initialize()
			{

				let $fabric = this.$refs.fabric;
				this.fabricCanvas = new fabric.Canvas($fabric);

				this.fabricCanvas.preserveObjectStacking = true; // keeps z-index the same even for active selection objects.
				this.fabricCanvas.uniformScaling = true; // objects can only be scaled proportionally
				this.fabricCanvas.fireRightClick = true; // enable firing of right click events

				this.fabricCanvas.setWidth(this.canvasWidth);
				this.fabricCanvas.setHeight(this.canvasHeight);

				this.reload();

			},

			reload()
			{

				// Add more objects to stage recursively, if necessary
				this.add(0, () => {

					// Update all objects recursively
					this.updateAll();

				});

			},

			updateAll()
			{

				// Update all objects recursively
				this.update(0, () => {
				});

			},

			// Add text / signature to stage
			add(index, callback)
			{

				let attributes = this.attributes[index];
				let offsetX = attributes.x ?? 0;
				let offsetY = attributes.y ?? 0;

				let params = {
					left: this.canvasWidth / 2 + offsetX,
					top: this.canvasHeight / 2 + offsetY,
					index: index,
				};

				let fabricObject = {};

				fabricObject = new fabric.Text('');

				fabricObject.set(params);

				this.fabricCanvas.add(fabricObject);

				// Add all other objects
				this.addNext(index + 1, callback);

			},

			// Add more objects recursively, if necessary
			addNext(i, callback)
			{
				if (i < this.count) {
					this.add(i, () => {
						callback && callback();
					});
				} else { // Otherwise when last object has been added, callback.
					callback && callback();
				}
			},

			update(index, callback)
			{

				let object = this.getObjects[index];
				let attributes = this.attributes[index];
				let box = null;

				this.loadFont(attributes, object, index, (attr, obj, fnt) => {

					// Params common to Image, Text, Signature
					let params = {
						evented: true,
						selectable: true,
					};

					let name = this.name === '' ? 'Julianne Michael' : this.name;
					let tagline = this.tagline === '' ? 'photography' : this.tagline;

					// Extend params for Text, Signature
					params = Object.assign({}, params, {
						text: (index === 1) ? tagline.toUpperCase() : name,
						fill: attributes.font_color ?? '#000000',
						fontFamily: fnt.name,
						fontSize: attributes.font_size ? attributes.font_size : (index === 0) ? 50 : 12,
						fontWeight: (attributes.font_weight) ? attributes.font_weight : 'normal',
						scaleX: attributes.scaleX ?? 1,
						scaleY: attributes.scaleY ?? 1,
						/*stroke: '#e2e8f0',
						strokeWidth: (index === 1) ? 3 : 0,
						paintFirst: "stroke",*/
					});

					// Update tagline position based on set signature baseline
					if (index === 1) {

						// Set fabric object params and prepare it for position calculation
						obj.set(params);

						// Get bounding box for tagline
						box = obj.getBoundingRect();

						// Set tagline w / h
						this.taglineWidth = Math.round(box.width);
						this.taglineHeight = Math.round(box.height);

						params.left = this.autoAlignX(fnt.id);
						params.top = this.autoAlignY();
					}

					// Set fabric object params and prepare it for position calculation
					obj.set(params);

					// Render canvas with updated object
					this.fabricCanvas.renderAll();

					// Get bounding box of text
					box = obj.getBoundingRect();

					// Ensure tagline is always in front
					if (index === 1) {

						obj.bringToFront();

						this.$emit('updateSample', this.fabricCanvas.toDataURL());

					}

					// Set signature baseline
					if (index === 0) {

						// Baseline offset
						const baseline = (attr.baseline !== undefined) ? attributes.baseline : 0;

						// Get context of rendered canvas
						this.signatureContext = this.fabricCanvas.getContext('2d');
						this.signatureBaseline = Math.round(box.top + box.height) + baseline;

						const img = new Image();

						img.src = this.fabricCanvas.toDataURL();

						img.onload = () => {

							let croppedBaselineCanvas = document.createElement('canvas');
							let croppedBaselineContext = croppedBaselineCanvas.getContext('2d');

							croppedBaselineCanvas.width = this.canvasWidth;
							croppedBaselineCanvas.height = 1; // extract only one pixel horizontally across the canvas at baseline position

							croppedBaselineContext.drawImage(img, 0, this.signatureBaseline, this.canvasWidth, 1, 0, 0, this.canvasWidth, 1);

							this.imageDataToScan = croppedBaselineContext.getImageData(0, 0, this.canvasWidth, 1);

							// Update all other objects on stage
							this.updateNext(index + 1, callback);

						};

					}

				});


			},

			// Update next fabric object on stage recursively.
			updateNext(i, callback)
			{
				if (callback) {
					if (i < this.count) {
						this.update(i, () => {
							callback();
						});
					} else {
						callback();
					}
				}
			},

			loadFont(attributes, object, index, callback)
			{

				const fontId = attributes.font_id ? attributes.font_id : (index === 1) ? 250 : 3;

				// Get the Font object
				let fonts = (index === 0) ? this.initialSignatureFonts : this.initialTextFonts;
				let font = fonts.find(i => i.id === fontId);

				// Load font
				FontLoader.load(font, () => {
					callback(attributes, object, font);
				});

			},

			// Calculates the best vertical position for tagline based on signature baseline
			autoAlignY()
			{
				let positionY = this.signatureBaseline + 10;

				return positionY;
			},

			// Calculates the best horizontal position for tagline based on signature filled pixels
			autoAlignX(fontId)
			{

				let positionsX = [];
				let positionX = this.canvasWidth / 2;
				let pixels = this.imageDataToScan.data;
				const padding = 10; // padding around tagline to give some space

				// Which vertical position to check baseline intersect
				const y = 0;
				let filled = [];

				// Iterate over the pixels at the specified vertical position
				for (let x = 0; x < this.canvasWidth; x++) {

					// Calculate the index of the pixel at the specified vertical position and current horizontal position
					const index = (y * this.canvasWidth + x) * 4;

					// Extract the alpha channel value (transparency) of the pixel
					const alpha = pixels[index + 3];

					// Check if the pixel is filled (non-transparent)
					if (alpha > 0) {

						// Push all filled pixels to array
						filled.push(x);

					}

				}

				// Add first and last pixel to filled array
				filled.push(this.canvasWidth);

				// Checks for available spot to place tagline. If Distance between last pixel and current pixel is larger or equal to tagline width, store x position.
				for (let i = 1; i < filled.length; i++) {

					if (filled[i] - filled[i - 1] >= this.taglineWidth + padding * 2) {

						// Available position to place tagline. Add half of width due to centered origin. Add some padding to space out tagline from signature a bit.
						let taglineX = filled[i - 1] + this.taglineWidth / 2 + padding;

						positionsX.push(taglineX);

					}

				}

				// Possible x positions to place tagline horizontally
				if (positionsX.length > 0) {
					positionX = positionsX[0];
				}

				return positionX;
			},

			download()
			{
				if (this.funnel === 'landing_signature') {
					location.href = '/register?plan=premium&funnel=landing_signature';
				} else {
					location.href = '/start';
				}
			}


		},

		mounted()
		{

			this.initialize();

		}

	}
</script>